/**
 * Session source attribution.
 *
 * Mirrors the existing `workout_sessions.challenge_day_id` pattern used for
 * Group challenges: the source link is resolved once, at session-creation
 * time, and stored on the row — never re-derived later by date-window
 * matching. That is what makes a session unambiguous afterwards even when a
 * user has several overlapping competition windows.
 *
 * Scope note: these columns are for *attribution* (history/source labels).
 * H2H and Pair scoring still aggregates over the challenge window, unchanged.
 */

type Clientish = {
  from: (table: string) => any;
};

export interface SessionAttribution {
  h2h_id: string | null;
  pair_challenge_id: string | null;
  /**
   * Group attribution reuses the long-standing
   * `workout_sessions.challenge_day_id` FK (-> group_challenge_days ->
   * group_challenges -> groups). No new column, no new pattern: the group a
   * session belongs to is derived from the challenge day it was started on.
   */
  challenge_day_id: string | null;
}

export const EMPTY_ATTRIBUTION: SessionAttribution = {
  h2h_id: null,
  pair_challenge_id: null,
  challenge_day_id: null,
};

/**
 * Resolve the active group-challenge day for ONE explicit group on a date.
 *
 * Scoped by an explicit `groupId` (the group currently selected in the home
 * switcher / the group whose shared plan is taking over the screen), never by
 * "any group the user happens to be in" — that is what would mix two groups'
 * sessions up when a member runs overlapping challenges.
 */
async function resolveGroupChallengeDay(
  client: Clientish,
  groupId: string,
  sessionDate: string,
): Promise<string | null> {
  const { data: chRows } = await client
    .from("group_challenges")
    .select("id, start_date, days")
    .eq("group_id", groupId)
    .eq("status", "active")
    .lte("start_date", sessionDate)
    .order("start_date", { ascending: false })
    .limit(5);

  const rows = (chRows ?? []) as Array<{ id: string; start_date: string; days: number }>;
  for (const ch of rows) {
    const dayNumber =
      Math.floor(
        (new Date(`${sessionDate}T00:00:00Z`).getTime() -
          new Date(`${ch.start_date}T00:00:00Z`).getTime()) /
          86_400_000,
      ) + 1;
    if (dayNumber < 1 || dayNumber > ch.days) continue;
    const { data: dayRow } = await client
      .from("group_challenge_days")
      .select("id")
      .eq("challenge_id", ch.id)
      .eq("day_number", dayNumber)
      .maybeSingle();
    const id = (dayRow as { id: string } | null)?.id ?? null;
    if (id) return id;
  }
  return null;
}


/**
 * Resolve which H2H and/or Pair challenge a session started on `sessionDate`
 * by `profileId` belongs to.
 *
 * A session can legitimately belong to one H2H *and* one Pair challenge at
 * the same time — they are separate competitions — so both columns are
 * resolved independently. When a user somehow has two live challenges of the
 * *same* kind covering the date, the most recently started one wins, so the
 * attribution is still deterministic rather than ambiguous.
 */
export async function resolveSessionAttribution(
  client: Clientish,
  profileId: string,
  sessionDate: string,
  groupId?: string | null,
): Promise<SessionAttribution> {
  const dayStart = `${sessionDate}T00:00:00Z`;
  const dayEnd = `${sessionDate}T23:59:59Z`;

  // Pair challenges are scoped explicitly by pair membership rather than
  // relying on RLS, because this helper also runs under the service-role
  // client (joint sessions), where RLS is bypassed.
  const myPairIds: string[] = await client
    .from("pairs")
    .select("id")
    .eq("status", "accepted")
    .or(`profile_a.eq.${profileId},profile_b.eq.${profileId}`)
    .then((r: any) => (r.data ?? []).map((p: any) => p.id))
    .catch(() => []);

  const [h2h, pair, groupDay] = await Promise.all([
    client
      .from("head_to_heads")
      .select("id, started_at")
      .or(`challenger_id.eq.${profileId},opponent_id.eq.${profileId}`)
      // Liveness guard, mirroring the Pair (`completed_at is null`) and Group
      // (`status = active`) branches below. A pending / declined / cancelled
      // challenge keeps its `started_at` + `ends_at` window and never gets an
      // `ended_at`, so the date window alone would attribute every solo
      // workout in that window to a match that never ran.
      .eq("status", "active")
      .is("ended_at", null)
      .lte("started_at", dayEnd)
      .gte("ends_at", dayStart)
      .order("started_at", { ascending: false })
      .limit(1)
      .then((r: any) => r.data?.[0]?.id ?? null)
      .catch(() => null),
    myPairIds.length === 0
      ? Promise.resolve(null)
      : client
          .from("pair_challenges")
          .select("id, starts_on")
          .in("pair_id", myPairIds)
          .is("completed_at", null)
          .lte("starts_on", sessionDate)
          .gte("ends_on", sessionDate)
          .order("starts_on", { ascending: false })
          .limit(1)
          .then((r: any) => r.data?.[0]?.id ?? null)
          .catch(() => null),
    groupId
      ? resolveGroupChallengeDay(client, groupId, sessionDate).catch(() => null)
      : Promise.resolve(null),
  ]);

  return { h2h_id: h2h, pair_challenge_id: pair, challenge_day_id: groupDay };
}

/** Never let attribution failures block starting a workout. */
export async function safeResolveSessionAttribution(
  client: Clientish,
  profileId: string,
  sessionDate: string,
  groupId?: string | null,
): Promise<SessionAttribution> {
  try {
    return await resolveSessionAttribution(client, profileId, sessionDate, groupId);
  } catch {
    return EMPTY_ATTRIBUTION;
  }
}

